import { useEffect, useState } from "react";
import {
  GoogleLoginLogo,
} from "../../assets";
import { getGoogleUrl } from "../../utils/getGoogleUrl";
import useLogin from "./useLogin";
import { useAuth } from "../../hooks/useAuth";
import { Input, Spin } from "antd";
import { MdOutlineAutoGraph } from "react-icons/md";
import { LoadingOutlined } from '@ant-design/icons';
import axiosInstance from "../../services/axios-client";

const Login = () => {
  const {
    loginHandler,
    onChangeHandler,
    loginError,
    setLoginError,
    loadingPagestate,
    setLoadingPagestate
  } = useLogin();
  
  const { login } = useAuth();

  const [externalPopup, setExternalPopup] = useState(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordError, setForgotPasswordError] = useState(false);
  const [forgotPasswordErrorMessage, setForgotPasswordErrorMessage] = useState('');
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const oauthStorageKey = 'googleOAuthResult';

  const handleForgotPassword = async (e) => {
    e.preventDefault()
    if (!forgotPasswordEmail) return
    
    try {
      setForgotPasswordLoading(true)
      setForgotPasswordError(false)
      setForgotPasswordSuccess(false)
      
      const frontEndApi = `${window.location.protocol}//${window.location.host}`
      
      const res = await axiosInstance.post('/forget-password', { 
        Email: forgotPasswordEmail.toLowerCase(),
        frontEndApi: frontEndApi
      })
      
      if (res.status === 200) {
        setForgotPasswordSuccess(true)
        setForgotPasswordEmail('')
      }
    } catch (error) {
      setForgotPasswordError(true)
      const errorMsg = error?.response?.data?.status?.description || error.message || 'Failed to send reset link. Please try again.'
      setForgotPasswordErrorMessage(errorMsg)
      console.error('Forgot password error:', errorMsg)
    } finally {
      setForgotPasswordLoading(false)
    }
  }

  const onGoogleLogin = () => {
    const width = 500;
    const height = 400;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2.5;
    const title = `GOOGLE Login`;
    const url = getGoogleUrl(window.location.origin);
    const popup = window.open(
      url,
      title,
      `width=${width},height=${height},left=${left},top=${top}`
    );
    setExternalPopup(popup);
  };

  useEffect(() => {
    const consumeOAuthResult = (payload) => {
      if (!payload) return;
      if (payload.type === 'GOOGLE_OAUTH_SUCCESS') {
        try {
          setLoadingPagestate(true);
          login(payload.token, payload.user);
          if (externalPopup && !externalPopup.closed) {
            externalPopup.close();
            setExternalPopup(null);
          }
        } catch (error) {
          setLoginError(true);
        } finally {
          setLoadingPagestate(false);
        }
      } else if (payload.type === 'GOOGLE_OAUTH_ERROR') {
        setLoginError(true);
        if (externalPopup && !externalPopup.closed) {
          externalPopup.close();
          setExternalPopup(null);
        }
      }
    };

    // Listen for messages from the OAuth callback
    const handleMessage = async (event) => {      
      // Verify the origin for security
      const allowedOrigins = [
        window.location.origin,
        process.env.REACT_APP_BACKEND_API_URL
      ];
      
      if (!allowedOrigins.includes(event.origin)) {
        console.log('Origin not allowed, ignoring message');
        return;
      }

      consumeOAuthResult(event.data);
    };

    // Listen for storage updates from OAuth callback (new tab flow)
    const handleStorage = (event) => {
      if (event.key !== oauthStorageKey || !event.newValue) return;
      try {
        const payload = JSON.parse(event.newValue);
        consumeOAuthResult(payload);
      } catch (error) {
        setLoginError(true);
      } finally {
        localStorage.removeItem(oauthStorageKey);
      }
    };

    // If the callback already wrote a result, consume it immediately
    const existingResult = localStorage.getItem(oauthStorageKey);
    if (existingResult) {
      try {
        consumeOAuthResult(JSON.parse(existingResult));
      } catch (error) {
        setLoginError(true);
      } finally {
        localStorage.removeItem(oauthStorageKey);
      }
    }

    window.addEventListener('message', handleMessage);
    window.addEventListener('storage', handleStorage);

    // Also check if popup was closed manually
    const checkPopupClosed = setInterval(() => {
      if (externalPopup && externalPopup.closed) {
        setExternalPopup(null);
        clearInterval(checkPopupClosed);
      }
    }, 1000);

    return () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('storage', handleStorage);
      clearInterval(checkPopupClosed);
    };
  }, [externalPopup, setLoadingPagestate, setLoginError, login]);

  return (
    <div className="container-fluid" style={{ height: "100vh", position: 'relative' }}>
      {/* Logo positioned at top right */}
      <div style={{
        position: 'absolute',
        top: '32px',
        right: '32px',
        zIndex: 10
      }}>
        <img 
          src="/logo_gowin.png" 
          alt="Gowin Logo" 
          style={{ 
            height: '52px',
            width: 'auto'
          }}
        />
      </div>
      
      <div className="row h-100 g-0">
        <div className="col-12 col-lg-6 d-flex flex-column justify-content-center align-items-center left-login" style={{ padding: '40px 60px' }}>
          <div style={{ maxWidth: '460px', width: '100%' }}>
            {/* Title Section */}
            <div className="d-flex justify-content-start align-items-center mb-5">
              <MdOutlineAutoGraph style={{fontSize: '56px', marginRight: '16px', color: "#1890ff"}}/>
              <h2 style={{ 
                fontWeight: '700', 
                fontSize: '32px',
                margin: 0,
                color: '#1a1a1a',
                letterSpacing: '-0.5px'
              }}>
                Calibration System
              </h2>
            </div>

            {!showForgotPassword ? (
              <>
                {/* Welcome Text */}
                <div style={{ marginBottom: '40px' }}>
                  <h3 style={{ 
                    fontSize: '24px', 
                    fontWeight: '600', 
                    color: '#1a1a1a',
                    marginBottom: '8px'
                  }}>
                    Welcome back
                  </h3>
                  <p style={{ 
                    fontSize: '15px', 
                    color: '#6b7280',
                    margin: 0
                  }}>
                    Please enter your credentials to continue
                  </p>
                </div>

                {/* Error Message */}
                {loginError && (
                  <div style={{
                    padding: '12px 16px',
                    backgroundColor: '#fee2e2',
                    borderLeft: '4px solid #ef4444',
                    borderRadius: '6px',
                    marginBottom: '24px'
                  }}>
                    <p style={{
                      color: '#dc2626', 
                      fontSize: '14px',
                      margin: 0,
                      lineHeight: '1.5',
                      fontWeight: '500'
                    }}>
                      Login failed. Please check your email and password and try again.
                    </p>
                  </div>
                )}
                
                {/* Form */}
                <form onSubmit={(e) => loginHandler(e)}>
                  <div style={{ marginBottom: '24px' }}>
                    <label 
                      htmlFor="Email" 
                      style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151',
                        marginBottom: '8px'
                      }}
                    >
                      Email Address
                    </label>
                    <Input
                      required
                      type="email"
                      className="login-form"
                      id="Email"
                      name="Email"
                      placeholder="Enter your email"
                      onChange={(e) => onChangeHandler(e)}
                      style={{
                        height: '44px',
                        fontSize: '15px',
                        borderRadius: '8px'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <label 
                      htmlFor="password" 
                      style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151',
                        marginBottom: '8px'
                      }}
                    >
                      Password
                    </label>      
                    <Input.Password
                      required
                      placeholder="Enter your password"
                      className="login-form"
                      id="password"
                      name="Password"
                      onChange={(e)=>onChangeHandler(e)}
                      style={{
                        height: '44px',
                        fontSize: '15px',
                        borderRadius: '8px'
                      }}
                    />
                  </div>

                  <div className="d-flex flex-row justify-content-end" style={{ marginBottom: '28px' }}>
                    <button 
                      type="button"
                      onClick={() => {
                        setLoginError(false)
                        setShowForgotPassword(true)
                        setForgotPasswordError(false)
                        setForgotPasswordSuccess(false)
                        setForgotPasswordErrorMessage('')
                      }}
                      style={{
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#1890ff',
                        cursor: 'pointer',
                        textDecoration: 'none',
                        background: 'none',
                        border: 'none',
                        padding: 0
                      }}
                      onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                      onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                    >
                      Forgot password?
                    </button>
                  </div>

                  <button
                    type="submit"
                    disabled={loadingPagestate}
                    style={{
                      width: '100%',
                      height: '48px',
                      fontSize: '16px',
                      fontWeight: '600',
                      borderRadius: '8px',
                      border: 'none',
                      background: loadingPagestate ? '#9ca3af' : 'linear-gradient(135deg, #02B4CC 0%, #0284C7 100%)',
                      color: 'white',
                      cursor: loadingPagestate ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 2px 4px rgba(2, 180, 204, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                    onMouseEnter={(e) => {
                      if (!loadingPagestate) {
                        e.target.style.transform = 'translateY(-2px)'
                        e.target.style.boxShadow = '0 4px 8px rgba(2, 180, 204, 0.3)'
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!loadingPagestate) {
                        e.target.style.transform = 'translateY(0)'
                        e.target.style.boxShadow = '0 2px 4px rgba(2, 180, 204, 0.2)'
                      }
                    }}
                  >
                    {loadingPagestate && <Spin indicator={<LoadingOutlined style={{ fontSize: 16, color: 'white' }} spin />} />}
                    {loadingPagestate ? 'Signing In...' : 'Sign In'}
                  </button>
                </form>

                {/* Divider */}
                <div style={{ 
                  margin: '32px 0',
                  position: 'relative',
                  textAlign: 'center'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: 0,
                    right: 0,
                    height: '1px',
                    backgroundColor: '#e5e7eb'
                  }}></div>
                  <span style={{
                    position: 'relative',
                    backgroundColor: 'white',
                    padding: '0 16px',
                    fontSize: '14px',
                    color: '#9ca3af',
                    fontWeight: '500'
                  }}>
                    or continue with
                  </span>
                </div>

                {/* Google Sign In */}
                <button
                  onClick={() => onGoogleLogin()}
                  style={{
                    width: '100%',
                    height: '48px',
                    backgroundColor: "white",
                    border: "1px solid #e5e7eb",
                    borderRadius: '8px',
                    outline: "none",
                    cursor: "pointer",
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s ease',
                    fontSize: '15px',
                    fontWeight: '500',
                    color: '#374151'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#f9fafb'
                    e.target.style.borderColor = '#d1d5db'
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'white'
                    e.target.style.borderColor = '#e5e7eb'
                  }}
                >
                  <img 
                    src={GoogleLoginLogo} 
                    alt="Google Logo"
                    style={{ marginRight: '12px', height: '20px' }}
                  />
                  Sign in with Google
                </button>
              </>
            ) : (
              <>
                {/* Back Button */}
                <button
                  onClick={() => {
                    setShowForgotPassword(false)
                    setForgotPasswordEmail('')
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#6b7280',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    padding: '8px 0',
                    marginBottom: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.color = '#1890ff'}
                  onMouseLeave={(e) => e.target.style.color = '#6b7280'}
                >
                  <span>←</span> Back to login
                </button>

                {/* Forgot Password Content */}
                <div style={{ marginBottom: '40px' }}>
                  <h3 style={{ 
                    fontSize: '24px', 
                    fontWeight: '600', 
                    color: '#1a1a1a',
                    marginBottom: '8px'
                  }}>
                    Reset Password
                  </h3>
                  <p style={{ 
                    fontSize: '15px', 
                    color: '#6b7280',
                    margin: 0,
                    lineHeight: '1.5'
                  }}>
                    Enter your email address and we'll send you a link to reset your password
                  </p>
                </div>

                {/* Success Message */}
                {forgotPasswordSuccess && (
                  <div style={{
                    padding: '12px 16px',
                    backgroundColor: '#d1fae5',
                    borderLeft: '4px solid #10b981',
                    borderRadius: '6px',
                    marginBottom: '24px'
                  }}>
                    <p style={{
                      color: '#065f46',
                      fontSize: '14px',
                      margin: 0,
                      lineHeight: '1.5',
                      fontWeight: '500'
                    }}>
                      Reset link sent! Please check your email.
                    </p>
                  </div>
                )}

                {/* Error Message */}
                {forgotPasswordError && (
                  <div style={{
                    padding: '12px 16px',
                    backgroundColor: '#fee2e2',
                    borderLeft: '4px solid #ef4444',
                    borderRadius: '6px',
                    marginBottom: '24px'
                  }}>
                    <p style={{
                      color: '#dc2626',
                      fontSize: '14px',
                      margin: 0,
                      lineHeight: '1.5',
                      fontWeight: '500'
                    }}>
                      {forgotPasswordErrorMessage}
                    </p>
                  </div>
                )}

                {/* Forgot Password Form */}
                <form onSubmit={handleForgotPassword}>
                  <div style={{ marginBottom: '28px' }}>
                    <label 
                      htmlFor="forgotEmail" 
                      style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151',
                        marginBottom: '8px'
                      }}
                    >
                      Email Address
                    </label>
                    <Input
                      required
                      type="email"
                      id="forgotEmail"
                      placeholder="Enter your email"
                      value={forgotPasswordEmail}
                      onChange={(e) => {
                        setForgotPasswordEmail(e.target.value)
                        setForgotPasswordError(false)
                        setForgotPasswordSuccess(false)
                      }}
                      disabled={forgotPasswordLoading}
                      style={{
                        height: '44px',
                        fontSize: '15px',
                        borderRadius: '8px'
                      }}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={forgotPasswordLoading}
                    style={{
                      width: '100%',
                      height: '48px',
                      fontSize: '16px',
                      fontWeight: '600',
                      borderRadius: '8px',
                      border: 'none',
                      background: forgotPasswordLoading ? '#9ca3af' : 'linear-gradient(135deg, #02B4CC 0%, #0284C7 100%)',
                      color: 'white',
                      cursor: forgotPasswordLoading ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 2px 4px rgba(2, 180, 204, 0.2)',
                      opacity: forgotPasswordLoading ? 0.7 : 1
                    }}
                    onMouseEnter={(e) => {
                      if (!forgotPasswordLoading) {
                        e.target.style.transform = 'translateY(-2px)'
                        e.target.style.boxShadow = '0 4px 8px rgba(2, 180, 204, 0.3)'
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!forgotPasswordLoading) {
                        e.target.style.transform = 'translateY(0)'
                        e.target.style.boxShadow = '0 2px 4px rgba(2, 180, 204, 0.2)'
                      }
                    }}
                  >
                    {forgotPasswordLoading ? 'Sending...' : 'Send Reset Link'}
                  </button>
                </form>

                {/* Help Text */}
                <div style={{
                  marginTop: '24px',
                  padding: '12px 16px',
                  backgroundColor: '#f0f9ff',
                  borderRadius: '6px',
                  borderLeft: '4px solid #1890ff'
                }}>
                  <p style={{
                    fontSize: '13px',
                    color: '#374151',
                    margin: 0,
                    lineHeight: '1.5'
                  }}>
                    <strong>Note:</strong> The reset link will be sent to your registered email address and will expire in 10 minutes.
                  </p>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Right Side - Decorative */}
        <div className="col-lg-6 d-none d-lg-flex flex-column justify-content-center align-items-center right-login" style={{
          background: 'linear-gradient(to bottom right, #02B4CC 0%, #0284C7 100%)',
          position: 'relative'
        }}>    
          <div style={{ position: 'relative', zIndex: 1 }}>
            <MdOutlineAutoGraph style={{
              fontSize: '320px', 
              color: "rgba(255, 255, 255, 0.35)",
              filter: 'drop-shadow(0 8px 16px rgba(0,0,0,0.1))'
            }}/>
          </div>
          <h3 style={{
            color: 'white',
            fontSize: '28px',
            fontWeight: '600',
            marginTop: '40px',
            textAlign: 'center',
            textShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            Performance Calibration
          </h3>
          <p style={{
            color: 'rgba(255, 255, 255, 0.9)',
            fontSize: '16px',
            textAlign: 'center',
            maxWidth: '400px',
            lineHeight: '1.6',
            marginTop: '12px'
          }}>
            Ensure consistency and fairness in employee evaluations across all business units
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;